import { useEffect, useState, useContext } from "react";
import {
  getqtestAllProjects,
  getqtestAllFields,
  updateTestCasesInqtest,
} from "../../../../services/webServices";
import FieldMapper from "../../../common-components/FieldMapper";
import QTestProjectsComponentPages from "../../../common-components/QTestProjectsComponentPages";
import { LoadingModalContext } from "../../ServicePage";
import LoadingModal from "../../../common-components/LoadingModal";
import MessagePopup from "../../../popup-components/MessagePopup";
import { MessagePopupContext } from "../../ServicePage";

const priorityMap = {
  low: "722",
  medium: "723",
  high: "724",
};

function getPriorityValue(priorityLabelOrCode) {
  if (!priorityLabelOrCode) return undefined;
  if (["722", "723", "724"].includes(String(priorityLabelOrCode))) {
    return String(priorityLabelOrCode);
  }
  const normalized = String(priorityLabelOrCode).trim().toLowerCase();
  return priorityMap[normalized] || String(priorityLabelOrCode);
}

function isPriorityField(fieldName) {
  if (!fieldName) return false;
  const normalized = String(fieldName).trim().toLowerCase();
  const priorityKeywords = ['priority', 'priorities', 'severity'];
  return priorityKeywords.some(keyword => normalized.includes(keyword));
}

function formatTestStepsArray(testCase) {
  if (!testCase) return [];
  let stepsRaw =
    testCase.test_steps ||
    testCase.steps ||
    testCase["Test Steps"] ||
    [];
  if (
    Array.isArray(stepsRaw) &&
    stepsRaw.every((s) => typeof s === "object" && s.description)
  ) {
    return stepsRaw.map((step) => ({
      description: String(step.description || ""),
      expected: "expected" in step ? String(step.expected || "") : "",
    }));
  }
  if (Array.isArray(stepsRaw)) {
    return stepsRaw
      .map((step, idx) => ({
        description:
          typeof step === "string"
            ? step
            : step.description ||
            step.content ||
            step.step ||
            step.name ||
            `Step ${idx + 1}`,
        expected: "",
      }))
      .filter((s) => !!s.description);
  }
  if (typeof stepsRaw === "string") {
    return stepsRaw
      .split(/\r?\n|(?=Step\s*\d+:)/gi)
      .map((desc) => desc.trim())
      .filter(Boolean)
      .map((desc) => ({
        description: desc,
        expected: "",
      }));
  }
  return [];
}

function extractQmentisFields(testCases) {
  const set = new Map();
  (Array.isArray(testCases) ? testCases : [testCases]).forEach((tc) => {
    Object.keys(tc).forEach((k) => {
      if (k === "properties" && Array.isArray(tc.properties)) {
        tc.properties.forEach((prop) => {
          if (prop && prop.field_name) {
            set.set(`properties.${prop.field_name}`, {
              label: prop.field_name,
              key: `properties.${prop.field_name}`,
            });
          }
        });
      } else {
        let fieldLabel;
        let fieldKey = k;
        if (k.toLowerCase() === "name") {
          fieldLabel = "Test Case Title";
          fieldKey = "name";
        } else if (k === "test_steps") {
          fieldLabel = "Test Steps";
        } else if (
          k.toLowerCase() === "test case id" ||
          k === "test_case_id"
        ) {
          return;
        } else {
          fieldLabel = k;
        }
        set.set(fieldKey, { label: fieldLabel, key: fieldKey });
      }
    });
  });
  return [...set.values()];
}

const issueTypeToApiType = {
  "Test Cases": "test-cases",
};
const normalizeIssueType = (type) => issueTypeToApiType[type];

const QTestSelectionPage = ({
  taskType,
  issueType,
  setQTestSelectionPageOpen,
  testCasesData,
  almConfig,
}) => {
  const [projectsList, setProjectsList] = useState([]);
  const { setMessagePopup, setMessage } = useContext(MessagePopupContext);
  const [selectedProject, setSelectedProject] = useState(null);
  const [proceedingMap, setProceedingMap] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [testCases, setTestCases] = useState(testCasesData);
  const [selectedMapping, setSelectedMapping] = useState(null);
  const [fieldMappingCompleted, setFieldMappingCompleted] = useState(false);
  const [importLoading, setImportLoading] = useState(null);
  const [qTestFields, setQTestFields] = useState([]);
  const { setLoadingState } = useContext(LoadingModalContext);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setImportLoading("Projects");
        const res = await getqtestAllProjects();
        setProjectsList(res.data || []);
      } catch (error) {
        console.error("Error fetching projects:", error);
      } finally {
        setImportLoading(null);
      }
    };
    fetchProjects();
  }, []);

  useEffect(() => {
    if (!!selectedProject) {
      handleExport();
    }
  }, [selectedProject]);

  // const handleAvailableFields = async () => {
  //   if (!selectedProject) return [];
  //   try {
  //     const normalizedIssueType = normalizeIssueType(issueType);
  //     const response = await getqtestAllFields(
  //       selectedProject.id,
  //       normalizedIssueType
  //     );
  //     let processedFields = [];
  //     if (response && response.fields && Array.isArray(response.fields)) {
  //       setQTestFields(response.fields);

  //       processedFields = response.fields
  //         .filter((field) => field.label && field.id != null)
  //         .map((field) => ({
  //           name: String(field.label).trim(),
  //           key: `qtest_${String(field.id)}`,
  //           originalField: field, 
  //         }));
  //     }
  //     const existingFieldNames = new Set(
  //       processedFields.map((f) => f.name.toLowerCase())
  //     );
  //     const additionalFields = [];
  //     if (!existingFieldNames.has("title")) {
  //       additionalFields.push({ name: "Title", key: "name" });
  //     }
  //     if (
  //       !existingFieldNames.has("test steps") &&
  //       !existingFieldNames.has("steps")
  //     ) {
  //       additionalFields.push({ name: "Test Steps", key: "test_steps" });
  //     }
  //     return [...additionalFields, ...processedFields];
  //   } catch (error) {
  //     console.error("Error fetching available fields:", error);
  //     return [];
  //   } finally {
  //     setImportLoading(null);
  //   }
  // };

  const normalizeTestCasesData = (data) => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (typeof data === "object") {
      const arrayKeys = Object.keys(data).filter(
        (k) => Array.isArray(data[k]) && data[k].length > 0
      );
      if (arrayKeys.length > 0) {
        return data[arrayKeys[0]];
      }
      if (data["title"]) {
        return [data];
      }
    }
    return [];
  };

  const getQTestFieldInfo = (fieldKey) => {
    if (!fieldKey.startsWith("qtest_")) return null;
    const fieldId = Number(fieldKey.replace("qtest_", ""));
    const field = qTestFields.find(f => f.id === fieldId);
    return field || null;
  };

  const processFieldValue = (value, qTestField, fieldId) => {
    if (value === undefined || value === null || value === "") return null;

    // Method 1: Check using qTestField if available
    if (qTestField && isPriorityField(qTestField.label)) {
      const mappedPriority = getPriorityValue(value);
      return mappedPriority || String(value);
    }

    // Method 2: If qTestField not available, check by fieldId in qTestFields array
    if (fieldId !== undefined && fieldId !== null && qTestFields.length > 0) {
      const field = qTestFields.find(f => f.id === Number(fieldId));
      if (field && isPriorityField(field.label)) {
        const mappedPriority = getPriorityValue(value);
        return mappedPriority || String(value);
      }
    }

    // Method 3: Check for common priority patterns in the value itself
    if (typeof value === 'string') {
      const normalizedValue = value.trim().toLowerCase();
      if (['low', 'medium', 'high', 'priority'].some(keyword => normalizedValue.includes(keyword))) {
        const mappedPriority = getPriorityValue(value);
        if (mappedPriority && mappedPriority !== value) {
          console.log(`Priority mapping applied: ${value} -> ${mappedPriority}`);
          return mappedPriority;
        }
      }
    }

    return String(value);
  };

  const mapTestCasesWithSelectedMapping = () => {
    if (!selectedMapping?.mapping) {
      console.warn("No mapping found");
      return [];
    }
    const normalizedData = normalizeTestCasesData(testCasesData);

    return normalizedData.map((testCase) => {
      const mappedTestCase = {
        name: "",
        properties: [],
        test_steps: [],
      };

      mappedTestCase.test_steps = formatTestStepsArray(testCase);

      const mappedPropertiesNames = new Set();
      Object.entries(selectedMapping.mapping).forEach(
        ([qmentisFieldKey, qTestFieldObj]) => {
          if (!qTestFieldObj) return;
          let value;
          if (qmentisFieldKey.startsWith("properties.")) {
            const propLabel = qmentisFieldKey.slice("properties.".length);
            if (Array.isArray(testCase.properties)) {
              const matchProp = testCase.properties.find(
                (p) => p.field_name === propLabel
              );
              if (matchProp) {
                value = matchProp.field_value;
                mappedPropertiesNames.add(propLabel);
              }
            }
          } else {
            value = testCase[qmentisFieldKey];
          }
          if (value === undefined || value === null || value === "") return;

          let fieldId;
          if (typeof qTestFieldObj === "object" && qTestFieldObj !== null) {
            fieldId = qTestFieldObj.key;
          } else {
            fieldId = qTestFieldObj;
          }

          if (fieldId.startsWith("qtest_")) {
            const currentFieldId = Number(fieldId.replace("qtest_", ""));
            const qTestField = getQTestFieldInfo(fieldId);

            const processedValue = processFieldValue(value, qTestField, currentFieldId);
            if (processedValue !== null) {
              console.log(`Mapped field ${qmentisFieldKey} -> ${fieldId}: ${value} -> ${processedValue}`);
              mappedTestCase.properties.push({
                field_id: currentFieldId,
                field_value: processedValue
              });
            }
          } else if (fieldId === "name" || fieldId === "title") {
            mappedTestCase.name = String(value);
          } else if (fieldId === "test_steps") {
            mappedTestCase.test_steps = formatTestStepsArray(testCase);
          }
        }
      );

      if (Array.isArray(testCase.properties)) {
        testCase.properties.forEach((p) => {
          if (
            !mappedPropertiesNames.has(p.field_name) &&
            p.field_id != null &&
            p.field_value
          ) {
            const propFieldId = Number(p.field_id);
            const qTestField = qTestFields.find(f => f.id === propFieldId);

            // FIXED: Use centralized processing function with fieldId fallback
            const processedValue = processFieldValue(p.field_value, qTestField, propFieldId);
            if (processedValue !== null) {
              console.log(`Unmapped property ${p.field_name} (ID: ${propFieldId}): ${p.field_value} -> ${processedValue}`);
              mappedTestCase.properties.push({
                field_id: propFieldId,
                field_value: processedValue,
              });
            }
          }
        });
      }

      if (!mappedTestCase.name) {
        mappedTestCase.name = testCase["title"] || `Test Case ${Date.now()}`;
      }
      if (!Array.isArray(mappedTestCase.test_steps)) {
        mappedTestCase.test_steps = [];
      }
      return mappedTestCase;
    });
  };

  const handleExport = async () => {
    try {
      setImportLoading("Exporting");
      // const cases = mapTestCasesWithSelectedMapping();
      // const payloadData = Array.isArray(cases) ? cases : [cases];
      const addRes = await updateTestCasesInqtest(
        selectedProject.id,
        selectedProject.name,
        testCasesData,
        null
      );
      if (!addRes || !addRes.data) {
        setErrorMessage("Invalid response from server. Please try again.");
        return;
      }
      const { totalFailed = 0, totalAdded = 0 } = addRes.data;
      if (totalFailed === testCasesData.length && totalAdded === 0) {
        setMessage(
          `All cases were rejected due to invalid or incomplete field mappings. Please update your mappings and try again.`
        );
      } else if (totalFailed > 0) {
        setMessage(
          `${totalFailed} test cases failed to export, the rest were added successfully.`
        );
      } else {
        setMessage(`All test cases added successfully.`);
      }
      setMessagePopup(true);
      setQTestSelectionPageOpen(false);
    } catch (error) {
      console.error("Error adding test cases:", error);
      if (error.response) {
        const errorMessage =
          error.response.data?.message ||
          error.response.data?.error ||
          "Failed to add test cases to QTest.";
        setErrorMessage(`API Error: ${errorMessage}`);
      } else if (error.request) {
        setErrorMessage("Network error. Please check your connection and try again.");
      } else {
        setErrorMessage("An unexpected error occurred. Please try again.");
      }
    } finally {
      setImportLoading(null);
    }
  };

  const handleBackToProjectSelection = () => {
    setSelectedProject(null);
    setProceedingMap(null);
    setFieldMappingCompleted(false);
    setSelectedMapping(null);
    setQTestFields([]);
  };

  return (
    <>
      {!selectedProject ? (
        <QTestProjectsComponentPages
          issueType={issueType}
          taskType={taskType}
          projectsLoading={importLoading === "Projects"}
          projectsList={projectsList}
          selectedProject={selectedProject}
          setSelectedProject={setSelectedProject}
          setOpenProjectPage={setQTestSelectionPageOpen}
        />
      ) : (
        <>
          <div style={{ padding: 20 }}>
            <h3>
              Ready to export test cases to QTest project: {selectedProject.name}
            </h3>
            {importLoading === "Exporting" && <p>Exporting in progress...</p>}
          </div>
        </>
      )}

      {importLoading && <LoadingModal open={true} />}

      {!!errorMessage && (
        <MessagePopup
          modalOpen={!!errorMessage}
          message={errorMessage}
          okayHandler={() => {
            setErrorMessage(null);
            setQTestSelectionPageOpen(false);
          }}
        />
      )}
    </>
  );
};

export default QTestSelectionPage;